/**
 * Reset Password Page
 * Lets a user set a new password using the token from the forgot-password email link.
 * URL: /reset-password?token=<token>
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../../../context/AuthContext';
import FormInput from '../../../../components/FormInput';
import { FormSkeleton } from '@/components/base/Skeleton';

const resetPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .required('New password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords do not match'),
});

type ResetPasswordForm = yup.InferType<typeof resetPasswordSchema>;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const { validateResetToken, confirmPasswordReset } = useAuth();

  // 'checking' while validating the token, 'valid' / 'invalid' afterwards
  const [tokenStatus, setTokenStatus] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  useEffect(() => {
    let active = true;
    const check = async () => {
      if (!token) {
        if (active) setTokenStatus('invalid');
        return;
      }
      try {
        const valid = await validateResetToken(token);
        if (active) setTokenStatus(valid ? 'valid' : 'invalid');
      } catch {
        if (active) setTokenStatus('invalid');
      }
    };
    check();
    return () => {
      active = false;
    };
  }, [token, validateResetToken]);

  const onSubmit = async (data: ResetPasswordForm) => {
    setIsLoading(true);
    setError('');
    try {
      await confirmPasswordReset(token, data.newPassword);
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
      // If the link became invalid/expired/used, surface the dedicated screen.
      const msg = (err.message || '').toLowerCase();
      if (msg.includes('expired') || msg.includes('invalid') || msg.includes('already been used')) {
        setTokenStatus('invalid');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const shellClass =
    'min-h-screen flex items-center justify-center bg-white sm:bg-gradient-to-br sm:from-blue-50 sm:to-indigo-100 dark:bg-gray-800 sm:dark:from-gray-900 sm:dark:to-gray-800 py-0 sm:py-12 px-0 sm:px-6 lg:px-8';
  const cardClass =
    'bg-white dark:bg-gray-800 p-6 sm:p-8 sm:rounded-xl sm:shadow-lg sm:border sm:border-gray-200 sm:dark:border-gray-700';

  // ---- Success ----
  if (resetSuccess) {
    return (
      <div className={shellClass}>
        <div className="max-w-md w-full h-screen sm:h-auto flex flex-col justify-center sm:block">
          <div className={`${cardClass} text-center h-full sm:h-auto flex flex-col justify-center`}>
            <div className="mx-auto h-10 w-10 sm:h-12 sm:w-12 bg-green-600 rounded-xl flex items-center justify-center mb-3 sm:mb-4">
              <i className="ri-checkbox-circle-line text-white text-xl sm:text-2xl"></i>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Password reset successful
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Invalid / expired token ----
  if (tokenStatus === 'invalid') {
    return (
      <div className={shellClass}>
        <div className="max-w-md w-full h-screen sm:h-auto flex flex-col justify-center sm:block">
          <div className={`${cardClass} text-center h-full sm:h-auto flex flex-col justify-center`}>
            <div className="mx-auto h-10 w-10 sm:h-12 sm:w-12 bg-red-600 rounded-xl flex items-center justify-center mb-3 sm:mb-4">
              <i className="ri-error-warning-line text-white text-xl sm:text-2xl"></i>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Link invalid or expired
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
              This password reset link is invalid, has expired, or has already been used. Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <i className="ri-refresh-line mr-2"></i>
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Checking token ----
  if (tokenStatus === 'checking') {
    return (
      <FormSkeleton />
    );
  }

  // ---- Valid token: show the form ----
  return (
    <div className={shellClass}>
      <div className="max-w-md w-full h-screen sm:h-auto overflow-y-auto sm:overflow-visible">
        <div className={`${cardClass} min-h-screen sm:min-h-0`}>
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center mb-3 sm:mb-4">
              <img
                src="/images/header/ap-logo-large.png"
                alt="Andhra Pradesh Government"
                className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
              />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Set a new password
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Choose a strong password you haven&apos;t used before
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-400 mr-2"></i>
                  <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                </div>
              </div>
            )}

            <FormInput
              label="New Password"
              type="password"
              icon={<i className="ri-lock-line"></i>}
              placeholder="Enter new password"
              error={errors.newPassword?.message}
              {...register('newPassword')}
            />

            <FormInput
              label="Confirm Password"
              type="password"
              icon={<i className="ri-lock-line"></i>}
              placeholder="Re-enter new password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Resetting password...
                </div>
              ) : (
                'Reset password'
              )}
            </button>

            <div className="text-center pt-2">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                Back to login
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
